import { useState } from 'react';
import { useLoaderData, useSearchParams } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';
import { Brain, TrendingUp, Clock, ThumbsDown } from 'lucide-react';
import { requireUser } from '../../../lib/session.server';
import { jobsApi, strategyApi } from '../../../lib/api';
import { Card, Button, Badge } from '../../../components/ui';
import { JobCard, JobDetailSheet } from '../../../components/jobs';
import { useToastActions } from '../../../components/ui';
import type { JobMatch, User } from '../../../types';

interface JobMatchesData {
  user: User;
  matches: JobMatch[];
  total_count: number;
  type: 'recommended' | 'recent' | 'rejected';
  attribution_summary?: {
    strategies_used: string[];
    exploration_rate: number;
  };
}

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await requireUser(request);
  const url = new URL(request.url);
  
  const type = (url.searchParams.get('tab') || 'recommended') as 'recommended' | 'recent' | 'rejected';
  const page = parseInt(url.searchParams.get('page') || '1', 10);

  try {
    const response = await jobsApi.getMatches({
      type,
      page,
      pageSize: 20,
      user_id: user.id,
    });

    return {
      user,
      matches: response.matches,
      total_count: response.total_count,
      type,
      attribution_summary: response.attribution_summary,
    };
  } catch (error) {
    return {
      user,
      matches: [],
      total_count: 0,
      type,
    };
  }
}

export function JobMatchesPage() {
  const { matches: initialMatches, total_count, type, attribution_summary } = useLoaderData() as JobMatchesData;
  const [searchParams, setSearchParams] = useSearchParams();
  const [matches, setMatches] = useState<JobMatch[]>(initialMatches);
  const [selectedJob, setSelectedJob] = useState<JobMatch | null>(null);
  const [showJobDetail, setShowJobDetail] = useState(false);
  // Initialize current page from URL params  
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  
  const { success, error } = useToastActions();

  const handleTabChange = (newTab: string) => {
    setSearchParams(prev => {
      prev.set('tab', newTab);
      prev.delete('page'); // Reset pagination when switching tabs
      return prev;
    });
  };

  const handleSave = async (jobId: string) => {
    try {
      const job = matches.find(j => j.id === jobId);
      if (job?.saved) {
        await jobsApi.unsaveJob(jobId);
        success('Job Unsaved', 'Job removed from saved jobs.');
      } else {
        await jobsApi.saveJob(jobId, { bucket: 'to_apply' });
        success('Job Saved', 'Job added to your saved jobs.');
      }
      
      // Record interaction for strategy learning
      await strategyApi.recordReward({
        user_id: 'current',
        job_id: jobId,
        action: job?.saved ? 'reject' : 'save',
        strategy_key: job?.attribution?.strategy_key,
        query_id: job?.attribution?.query_id,
      });
      
      setMatches(matches.map(j => 
        j.id === jobId ? { ...j, saved: !j.saved } : j
      ));
    } catch (err) {
      error('Action Failed', 'Unable to save job. Please try again.');
    }
  };

  const handleReject = async (jobId: string) => {
    try {
      await jobsApi.rejectJob(jobId);
      
      // Record rejection for strategy learning
      const job = matches.find(j => j.id === jobId);
      await strategyApi.recordReward({
        user_id: 'current',
        job_id: jobId,
        action: 'reject',
        strategy_key: job?.attribution?.strategy_key,
        query_id: job?.attribution?.query_id,
      });
      
      setMatches(matches.filter(j => j.id !== jobId));
      success('Job Rejected', 'This job will not appear in future recommendations.');
    } catch (err) {
      error('Action Failed', 'Unable to reject job. Please try again.');
    }
  };

  const handleApply = async (jobId: string) => {
    const job = matches.find(j => j.id === jobId);
    if (job?.url) {
      // Record application intent for strategy learning
      await strategyApi.recordReward({
        user_id: 'current',
        job_id: jobId,
        action: 'apply',
        strategy_key: job.attribution?.strategy_key,
        query_id: job.attribution?.query_id,
      });
      
      window.open(job.url, '_blank');
    }
  };

  const handleJobClick = async (jobId: string) => {
    const job = matches.find(j => j.id === jobId);
    if (job) {
      setSelectedJob(job);
      setShowJobDetail(true);
      
      // Record view for analytics
      await jobsApi.recordView(jobId, {
        source: 'matches',
        strategy_key: job.attribution?.strategy_key,
        query_id: job.attribution?.query_id,
      });
    }
  };

  const tabs = [
    { key: 'recommended', label: 'Recommended', icon: Brain },
    { key: 'recent', label: 'Recently Viewed', icon: Clock },
    { key: 'rejected', label: 'Rejected', icon: ThumbsDown },
  ];

  const activeTab = tabs.find(tab => tab.key === type) || tabs[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Brain className="h-6 w-6 text-primary" />
          <h1 className="text-3xl font-bold text-foreground">Job Matches</h1>
        </div>
        <p className="text-muted-foreground">
          AI-powered job recommendations based on your strategy and preferences.
        </p>
      </div>

      {/* Attribution Summary */}
      {attribution_summary && type === 'recommended' && (
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-foreground">Strategy Insights</h3>
              <p className="text-sm text-muted-foreground">
                Recommendations powered by {attribution_summary.strategies_used.length} strategies
              </p>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Exploration Rate:</span>
                <span className="ml-1 font-medium text-foreground">
                  {(attribution_summary.exploration_rate * 100).toFixed(0)}%
                </span>
              </div>
              <Badge variant="secondary">
                {attribution_summary.strategies_used.length} strategies active
              </Badge>
            </div>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <Card>
        <div className="flex items-center gap-1 p-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.key === type;
            
            return (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Match Results */}
      {matches.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <activeTab.icon className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              No {activeTab.label.toLowerCase()} jobs
            </h3>
            <p className="text-muted-foreground mb-4">
              {type === 'recommended' 
                ? 'Complete your profile and search for jobs to get personalized recommendations.'
                : type === 'recent'
                ? 'Jobs you view will appear here for easy access.'
                : 'Jobs you reject will be listed here.'}
            </p>
            {type === 'recommended' && (
              <Button onClick={() => window.location.href = '/search-jobs'}>
                Search Jobs
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              <strong className="text-foreground">{total_count}</strong> {activeTab.label.toLowerCase()} jobs
              {total_count > 0 && (
                <span className="ml-2">• Page {currentPage}</span>
              )}
            </div>
            {type === 'recommended' && (
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                <span className="text-sm text-muted-foreground">
                  Ranked by match score
                </span>
              </div>
            )}
          </div>

          <div className="space-y-4">
            {matches.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                variant="detailed"
                onSave={type !== 'rejected' ? handleSave : undefined}
                onReject={type === 'recommended' ? handleReject : undefined}
                onApply={type !== 'rejected' ? handleApply : undefined}
                onClick={handleJobClick}
              />
            ))}
          </div>

          {/* Show strategy performance hint for low match scores */}
          {type === 'recommended' && matches.some(job => job.match_score < 0.3) && (
            <Card>
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <TrendingUp className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-foreground mb-1">Improve Your Matches</h4>
                    <p className="text-sm text-muted-foreground">
                      Some matches have low scores. Consider updating your strategy objectives or 
                      adding more detail to your resume to get better recommendations.
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" variant="secondary" onClick={() => window.location.href = '/strategy/objectives'}>
                        Tune Strategy
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => window.location.href = '/resumes'}>
                        Update Resume
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </>
      )}

      {/* Job Detail Sheet */}
      <JobDetailSheet
        job={selectedJob}
        isOpen={showJobDetail}
        onClose={() => {
          setShowJobDetail(false);
          setSelectedJob(null);
        }}
        onSave={type !== 'rejected' ? handleSave : undefined}
        onReject={type === 'recommended' ? handleReject : undefined}
        onApply={type !== 'rejected' ? handleApply : undefined}
      />
    </div>
  );
}